/**
 * CHROME MOTION (B10) — the layout's fallback for chrome that does not manage itself.
 *
 * Slots are `display: contents` wrappers ([data-ld-slot="panel|card|hud|sheet"]),
 * so every module keeps positioning itself against the root. The layout never
 * writes the modules' own `transition`, `transform` or `opacity` declarations:
 * everything here is a Web Animation (removable) or the `inert` attribute, and
 * both are undone exactly (only what the layout added is removed).
 *
 * SELF-MANAGED chrome is left alone: a slot child that is (or contains) an
 * `.ld-ui-in` element, or carries data-ld-chrome="self" / data-ld-arrive="self".
 * The places panel (B7) and the hud (B9) are self-managed: they arrive on the
 * "ui" beat with .ld-ui-in, and dim to .35 + inert in a drive, the zoom island
 * excepted. The fallback exists so any other chrome follows the same rules:
 *
 *  Arrival (§E.7): unmanaged children of the panel and hud slots fade and rise
 *  12 px over 640 settle, stagger 70 in DOM order (panel first); no stagger for
 *  the short intro; a 160 ms fade under reduced motion.
 *
 *  Drive (§E.6): unmanaged children of the panel and hud slots go to .35 over
 *  420 drift and become inert, except [data-ld-drive="live"]; inside a live
 *  child, [data-ld-drive="dim"] elements dim. Children mounted during the drive
 *  dim at once. The card slot only goes inert (the card plays its own exit and
 *  must not be held at .35). The mobile sheet steps aside by itself (BottomSheet).
 *  The overlays slot (DriveHud, toasts) is never touched.
 *
 *  Attract (§G): the panel and sheet slots' children are inert (not dimmed).
 */
import { useEffect, useRef, type RefObject } from "react";
import { EASE_CSS } from "@/design";
import { ARRIVAL, ARRIVAL_REDUCED_MS, DRIVE_DIM, type ChromeState, type UiState } from "./arrival";

const SLOT = (name: string) => `:scope [data-ld-slot="${name}"]`;

function childrenOf(root: HTMLElement, slot: string): HTMLElement[] {
  const out: HTMLElement[] = [];
  root.querySelectorAll<HTMLElement>(SLOT(slot)).forEach((s) => {
    for (const c of Array.from(s.children)) if (c instanceof HTMLElement) out.push(c);
  });
  return out;
}

/** A module that runs its own arrival and drive state. */
export function selfManaged(el: HTMLElement): boolean {
  if (el.dataset.ldChrome === "self" || el.dataset.ldArrive === "self") return true;
  return el.classList.contains("ld-ui-in") || el.querySelector(".ld-ui-in") !== null;
}

/** What a drive does to the chrome: dim (+ inert) or only inert. */
export function driveTargets(root: HTMLElement): { dim: HTMLElement[]; inert: HTMLElement[] } {
  const dim = new Set<HTMLElement>();
  for (const slot of ["panel", "hud"]) {
    for (const c of childrenOf(root, slot)) {
      if (selfManaged(c)) continue;
      if (c.dataset.ldDrive === "live") c.querySelectorAll<HTMLElement>('[data-ld-drive="dim"]').forEach((d) => dim.add(d));
      else dim.add(c);
    }
  }
  return { dim: [...dim], inert: childrenOf(root, "card") };
}

/** Elements that sleep (inert) in kiosk attract. */
function attractTargets(root: HTMLElement): HTMLElement[] {
  return [...childrenOf(root, "panel"), ...childrenOf(root, "sheet")];
}

/** Elements that take the layout's arrival, in stagger order. */
function arrivalTargets(root: HTMLElement): HTMLElement[] {
  return [...childrenOf(root, "panel"), ...childrenOf(root, "hud")].filter((el) => !selfManaged(el));
}

const canAnimate = (el: HTMLElement): boolean => typeof el.animate === "function";

export interface ChromeMotionInput {
  ui: UiState;
  stagger: boolean;
  chrome: ChromeState;
  reduced: boolean;
  /** Changes when the slots' content is swapped (layout variant). */
  variant: string;
}

export function useChromeMotion(rootRef: RefObject<HTMLElement | null>, input: ChromeMotionInput): void {
  const { ui, stagger, chrome, reduced, variant } = input;
  const prevUi = useRef<UiState>("hidden");

  /* ── arrival ─────────────────────────────────────────────────── */
  useEffect(() => {
    const root = rootRef.current;
    const was = prevUi.current;
    prevUi.current = ui;
    if (!root || ui !== "arriving" || was !== "hidden") return undefined;
    const anims: Animation[] = [];
    arrivalTargets(root).forEach((el, i) => {
      if (!canAnimate(el)) return;
      anims.push(
        reduced
          ? el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: ARRIVAL_REDUCED_MS, easing: "linear", fill: "backwards" })
          : el.animate([{ opacity: 0, translate: `0 ${ARRIVAL.rise}px` }, { opacity: 1, translate: "0 0" }], {
              duration: ARRIVAL.ms,
              easing: EASE_CSS.settle,
              delay: stagger ? i * ARRIVAL.stagger : 0,
              fill: "backwards",
            }),
      );
    });
    // Leaving mid-arrival (back to loading) must not leave elements half-faded.
    return () => {
      if (prevUi.current === "hidden") anims.forEach((a) => a.cancel());
    };
  }, [rootRef, ui, stagger, reduced]);

  /* ── drive dim + attract sleep ───────────────────────────────── */
  const dimmed = useRef(new Map<HTMLElement, Animation | null>());
  const inerted = useRef(new Set<HTMLElement>());

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const dimMap = dimmed.current;
    const inertSet = inerted.current;
    const dur = reduced ? 160 : DRIVE_DIM.ms;
    const easing = reduced ? "linear" : EASE_CSS.drift;

    const setInert = (el: HTMLElement) => {
      if (el.inert) return; // already inert (the module's own, or ours)
      el.inert = true;
      inertSet.add(el);
    };
    const dim = (el: HTMLElement, animate: boolean) => {
      if (dimMap.has(el)) return;
      setInert(el);
      if (!canAnimate(el)) {
        dimMap.set(el, null);
        return;
      }
      const from = getComputedStyle(el).opacity;
      dimMap.set(el, el.animate([{ opacity: from }, { opacity: String(DRIVE_DIM.opacity) }], { duration: animate ? dur : 0, easing, fill: "forwards" }));
    };
    const undim = () => {
      dimMap.forEach((anim, el) => {
        if (!anim) return;
        const cur = el.isConnected ? getComputedStyle(el).opacity : "1";
        anim.cancel();
        if (!el.isConnected || !canAnimate(el)) return;
        const base = getComputedStyle(el).opacity;
        if (cur !== base) el.animate([{ opacity: cur }, { opacity: base }], { duration: dur, easing });
      });
      dimMap.clear();
    };
    const wake = () => {
      inertSet.forEach((el) => {
        el.inert = false;
      });
      inertSet.clear();
    };
    const drive = (animate: boolean) => {
      const t = driveTargets(root);
      t.dim.forEach((el) => dim(el, animate));
      t.inert.forEach(setInert);
    };

    // Only the chrome slots are watched (the map and the overlays change freely during a drive).
    let observer: MutationObserver | null = null;
    const watch = (fn: () => void) => {
      if (typeof MutationObserver === "undefined") return;
      observer = new MutationObserver(fn);
      root.querySelectorAll<HTMLElement>(":scope [data-ld-slot]").forEach((s) => {
        const name = s.dataset.ldSlot;
        if (name !== "overlays" && name !== "map") observer!.observe(s, { childList: true });
      });
    };
    if (chrome === "dim") {
      drive(true);
      watch(() => drive(false));
    } else if (chrome === "asleep") {
      attractTargets(root).forEach(setInert);
      watch(() => attractTargets(root).forEach(setInert));
    }
    return () => {
      observer?.disconnect();
      undim();
      wake();
    };
  }, [rootRef, chrome, reduced, variant]);
}
